/**
 * Supported languages configuration
 * Each language has a code (ISO 639-1), native name, English name, and country code for flag SVGs.
 */

export interface Language {
  code: string;
  name: string;
  nameEn: string;
  country: string; // ISO 3166-1 alpha-2 for flag SVG lookup
}

export const SUPPORTED_LANGUAGES: Language[] = [
  // Europa
  { code: 'de', name: 'Deutsch', nameEn: 'German', country: 'DE' },
  { code: 'en', name: 'English', nameEn: 'English', country: 'GB' },
  { code: 'fr', name: 'Français', nameEn: 'French', country: 'FR' },
  { code: 'es', name: 'Español', nameEn: 'Spanish', country: 'ES' },
  { code: 'it', name: 'Italiano', nameEn: 'Italian', country: 'IT' },
  { code: 'pt', name: 'Português', nameEn: 'Portuguese', country: 'PT' },
  { code: 'nl', name: 'Nederlands', nameEn: 'Dutch', country: 'NL' },
  { code: 'pl', name: 'Polski', nameEn: 'Polish', country: 'PL' },
  { code: 'cs', name: 'Čeština', nameEn: 'Czech', country: 'CZ' },
  { code: 'sk', name: 'Slovenčina', nameEn: 'Slovak', country: 'SK' },
  { code: 'da', name: 'Dansk', nameEn: 'Danish', country: 'DK' },
  { code: 'sv', name: 'Svenska', nameEn: 'Swedish', country: 'SE' },
  { code: 'fi', name: 'Suomi', nameEn: 'Finnish', country: 'FI' },
  { code: 'el', name: 'Ελληνικά', nameEn: 'Greek', country: 'GR' },
  { code: 'hu', name: 'Magyar', nameEn: 'Hungarian', country: 'HU' },
  { code: 'ro', name: 'Română', nameEn: 'Romanian', country: 'RO' },
  { code: 'bg', name: 'Български', nameEn: 'Bulgarian', country: 'BG' },
  { code: 'et', name: 'Eesti', nameEn: 'Estonian', country: 'EE' },
  { code: 'lv', name: 'Latviešu', nameEn: 'Latvian', country: 'LV' },
  { code: 'lt', name: 'Lietuvių', nameEn: 'Lithuanian', country: 'LT' },
  { code: 'sl', name: 'Slovenščina', nameEn: 'Slovenian', country: 'SI' },
  { code: 'nb', name: 'Norsk', nameEn: 'Norwegian', country: 'NO' },
  { code: 'hr', name: 'Hrvatski', nameEn: 'Croatian', country: 'HR' },
  { code: 'sr', name: 'Srpski', nameEn: 'Serbian', country: 'RS' },
  { code: 'uk', name: 'Українська', nameEn: 'Ukrainian', country: 'UA' },
  { code: 'is', name: 'Íslenska', nameEn: 'Icelandic', country: 'IS' },
  { code: 'ga', name: 'Gaeilge', nameEn: 'Irish', country: 'IE' },
  { code: 'sq', name: 'Shqip', nameEn: 'Albanian', country: 'AL' },
  { code: 'mk', name: 'Македонски', nameEn: 'Macedonian', country: 'MK' },
  { code: 'bs', name: 'Bosanski', nameEn: 'Bosnian', country: 'BA' },
  { code: 'mt', name: 'Malti', nameEn: 'Maltese', country: 'MT' },
  { code: 'ka', name: 'ქართული', nameEn: 'Georgian', country: 'GE' },
  { code: 'hy', name: 'Հայերեն', nameEn: 'Armenian', country: 'AM' },
  { code: 'be', name: 'Беларуская', nameEn: 'Belarusian', country: 'BY' },
  // Asien
  { code: 'ja', name: '日本語', nameEn: 'Japanese', country: 'JP' },
  { code: 'zh', name: '中文', nameEn: 'Chinese', country: 'CN' },
  { code: 'ko', name: '한국어', nameEn: 'Korean', country: 'KR' },
  { code: 'hi', name: 'हिन्दी', nameEn: 'Hindi', country: 'IN' },
  { code: 'th', name: 'ไทย', nameEn: 'Thai', country: 'TH' },
  { code: 'vi', name: 'Tiếng Việt', nameEn: 'Vietnamese', country: 'VN' },
  { code: 'id', name: 'Bahasa Indonesia', nameEn: 'Indonesian', country: 'ID' },
  { code: 'ms', name: 'Bahasa Melayu', nameEn: 'Malay', country: 'MY' },
  { code: 'tl', name: 'Filipino', nameEn: 'Filipino', country: 'PH' },
  { code: 'km', name: 'ភាសាខ្មែរ', nameEn: 'Khmer', country: 'KH' },
  { code: 'bn', name: 'বাংলা', nameEn: 'Bengali', country: 'BD' },
  { code: 'ne', name: 'नेपाली', nameEn: 'Nepali', country: 'NP' },
  { code: 'si', name: 'සිංහල', nameEn: 'Sinhala', country: 'LK' },
  { code: 'mn', name: 'Монгол', nameEn: 'Mongolian', country: 'MN' },
  { code: 'kk', name: 'Қазақша', nameEn: 'Kazakh', country: 'KZ' },
  { code: 'uz', name: 'Oʻzbekcha', nameEn: 'Uzbek', country: 'UZ' },
  { code: 'my', name: 'မြန်မာဘာသာ', nameEn: 'Burmese', country: 'MM' },
  // Naher Osten
  { code: 'ar', name: 'العربية', nameEn: 'Arabic', country: 'SA' },
  { code: 'he', name: 'עברית', nameEn: 'Hebrew', country: 'IL' },
  { code: 'tr', name: 'Türkçe', nameEn: 'Turkish', country: 'TR' },
  { code: 'az', name: 'Azərbaycan', nameEn: 'Azerbaijani', country: 'AZ' },
  // Afrika
  { code: 'sw', name: 'Kiswahili', nameEn: 'Swahili', country: 'KE' },
  { code: 'am', name: 'አማርኛ', nameEn: 'Amharic', country: 'ET' },
  // Ozeanien
  { code: 'mi', name: 'Māori', nameEn: 'Maori', country: 'NZ' },
];

/** All supported language codes */
export const LANGUAGE_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

/** Map from language code to Language object */
export const LANGUAGE_MAP = new Map(SUPPORTED_LANGUAGES.map((l) => [l.code, l]));
